// Per-contact review only where it's needed (Phase 3.8): held-back values, customized emails and signal lines.
// A approve · R reject (remove from campaign) · E edit · J / K next / previous.
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, useApi } from "../lib/api";
import { Badge, Button, Card, Empty, ErrorNote, Field, inputCls, Loading, Notice, PageHeader, StickyBar, useAction } from "../components/ui";

export default function Exceptions() {
  const { id } = useParams();
  const { data, error, loading, reload } = useApi<any>(`/campaigns/${id}/exceptions`, [id]);
  const [i, setI] = useState(0);
  const [editing, setEditing] = useState(false);
  const [vals, setVals] = useState<any>({});
  // Removing can't be undone: the first R (or click) arms it, a second one within 3 seconds removes.
  const [armed, setArmed] = useState(false);
  const act = useAction();
  const items: any[] = (data?.items ?? []).filter((x: any) => x.status === "Enrolled");
  const item = items[i];
  const pendingCount = items.filter((x) => x.draftStatus !== "approved").length;

  useEffect(() => {
    setEditing(false);
    if (item) setVals({ first_name: item.merge?.first_name ?? "", company: item.merge?.company ?? "", title: item.merge?.title ?? "", signal_line: item.merge?.signal_line ?? "" });
  }, [item?.contactId]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = (d: number) => setI((x) => Math.min(Math.max(0, x + d), Math.max(0, items.length - 1)));
  const approve = () => item && !item.blocker && act.run(async () => { await api(`/enrollments/${id}/${item.contactId}/approve`, { body: {} }); await reload(); });
  const remove = () => {
    if (!item) return;
    if (!armed) {
      setArmed(true);
      setTimeout(() => setArmed(false), 3000);
      return;
    }
    setArmed(false);
    void act.run(async () => { await api(`/enrollments/${id}/${item.contactId}/remove`, { body: { reason: "removed in review" } }); await reload(); });
  };
  const saveVals = () => item && act.run(async () => { await api(`/enrollments/${id}/${item.contactId}/merge`, { method: "PUT", body: vals }); setEditing(false); await reload(); });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.closest("input, textarea, select") || e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === "a") approve();
      else if (k === "r") remove();
      else if (k === "e") setEditing(true);
      else if (k === "j") go(1);
      else if (k === "k") go(-1);
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (loading && !data) return <Loading rows={8} />;
  if (!data) return <ErrorNote error={error} />;
  return (
    <div className="space-y-5">
      <PageHeader
        title="Review contacts"
        sub={<Link className="text-accent-text hover:underline" to={`/campaigns/${id}`}>Back to the campaign</Link>}
        actions={<span className="text-sm text-muted">{items.length - pendingCount} of {items.length} reviewed</span>}
      />
      {!data.campaignApproved && <Notice tone="warn">Approve the campaign first; these contacts are approved one by one afterwards.</Notice>}
      <div className="h-1.5 overflow-hidden rounded-full bg-surface-2" aria-hidden>
        <div className="h-full bg-accent" style={{ width: `${items.length ? ((items.length - pendingCount) / items.length) * 100 : 100}%` }} />
      </div>
      <ErrorNote error={act.error} />
      {!item ? (
        <Card>
          <Empty title="Nothing to review" action={<Link to={`/campaigns/${id}`}><Button tone="primary">Back to the campaign</Button></Link>}>
            Every held, customized and signal-line contact in this campaign has been handled.
          </Empty>
        </Card>
      ) : (
        <Card
          title={
            <span className="flex flex-wrap items-center gap-2">
              <Link className="hover:underline" to={`/campaigns/${id}/contacts/${item.contactId}`}>{item.contactName}</Link>
              <span className="font-normal text-muted">{item.title} · {item.accountName}</span>
            </span>
          }
          actions={<span className="flex items-center gap-2">{item.draftStatus === "approved" ? <Badge tone="approved">Approved</Badge> : <Badge tone={item.review.kind}>{item.review.text}</Badge>}<span className="text-xs text-muted">{i + 1} / {items.length}</span></span>}
        >
          {item.review.kind === "held" && <Notice tone="warn">Held back: {item.review.text}. Fix the value (E) before approving.</Notice>}
          {editing ? (
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <Field label="First name"><input className={inputCls} value={vals.first_name} onChange={(e) => setVals({ ...vals, first_name: e.target.value })} autoFocus /></Field>
              <Field label="Company (as written in the email)"><input className={inputCls} value={vals.company} onChange={(e) => setVals({ ...vals, company: e.target.value })} /></Field>
              <Field label="Title"><input className={inputCls} value={vals.title} onChange={(e) => setVals({ ...vals, title: e.target.value })} /></Field>
              <div className="sm:col-span-3">
                <Field label="Signal line (leave empty for none)" help="{company} is replaced with the company name.">
                  <textarea className={inputCls} rows={2} value={vals.signal_line} onChange={(e) => setVals({ ...vals, signal_line: e.target.value })} />
                </Field>
              </div>
              <div className="flex gap-2 sm:col-span-3">
                <Button tone="primary" disabled={act.busy} onClick={saveVals}>Save</Button>
                <Button onClick={() => setEditing(false)}>Cancel</Button>
                <Link to={`/campaigns/${id}/contacts/${item.contactId}`} className="ml-auto"><Button tone="ghost">Edit this contact's emails</Button></Link>
              </div>
            </div>
          ) : (
            item.email1 && (
              <div className="mt-3 rounded-lg border border-line bg-surface-2 p-4">
                <div className="mb-2 text-sm"><span className="text-muted">Subject </span><span className="font-medium">{item.email1.subject}</span></div>
                <div className="mail text-sm">{item.email1.body}</div>
              </div>
            )
          )}
          {(item.leak?.blocks?.length > 0 || item.leak?.warnings?.length > 0) && (
            <ul className="mt-3 space-y-0.5 text-xs">
              {item.leak.blocks.map((f: any, j: number) => <li key={`b${j}`} className="text-bad">{f.where}: {f.rule}{f.match ? ` “${f.match}”` : ""}</li>)}
              {item.leak.warnings.map((f: any, j: number) => <li key={`w${j}`} className="text-warn">{f.where}: {f.rule}{f.match ? ` “${f.match}”` : ""}</li>)}
            </ul>
          )}
        </Card>
      )}
      {item && (
        <StickyBar>
          <span className="mr-auto hidden text-xs text-muted sm:block">A approve · R reject · E edit · J/K next/previous</span>
          <Button tone="ghost" onClick={() => go(-1)} disabled={i === 0}>Previous</Button>
          <Button onClick={() => go(1)} disabled={i >= items.length - 1}>Skip</Button>
          <Button tone={armed ? "danger" : "default"} onClick={remove} disabled={act.busy}>{armed ? "Remove from campaign?" : "Reject"}</Button>
          <Button onClick={() => setEditing(true)}>Edit</Button>
          <Button tone="primary" disabled={act.busy || !!item.blocker} title={item.blocker ?? ""} onClick={() => act.run(async () => { await api(`/enrollments/${id}/${item.contactId}/approve`, { body: {} }); await reload(); go(1); })}>
            Approve and next
          </Button>
        </StickyBar>
      )}
    </div>
  );
}
